import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TCenter } from '@shared/interfaces/TCenter';
import { TProject } from '@shared/interfaces/TProject';
import { momentumLongDesc, momentumShortDesc } from 'src/app/constants/momentum-description.constant';
import { addSmart } from 'src/app/helpers/add-smart.helper';
import { calculatePercent } from 'src/app/helpers/calculate-percent-helper';
import { divideSmart } from 'src/app/helpers/divide-smart.helper';
import { AccountLeaderboardItem, AccountLeaderboardTableData } from 'src/app/interfaces/account-leaderboard-table-data.interface';
import { CategoriesService } from 'src/app/services/categories.service';
import { PagingService } from 'src/app/services/paging.service';
import { KamReportsService } from '../../services/kam-reports.service';

@Component({
  selector: 'app-account-leaderboard',
  templateUrl: './account-leaderboard.component.html',
  styleUrls: ['./account-leaderboard.component.scss']
})
export class AccountLeaderboardComponent implements OnInit, AfterViewInit {

  @Input() project: TProject;
  @ViewChild(MatSort) accountLeaderboardSort: MatSort;
  @ViewChild(MatPaginator) accountLeaderboardPaginator: MatPaginator;
  public momentumLongDesc: string = momentumLongDesc;
  public momentumShortDesc: string = momentumShortDesc;
  public accountLeaderboardData: AccountLeaderboardTableData;
  public accountLeaderBoardDisplayedColumns: string[] = [
    'name',
    'total',
    'delta4Month',
    'deltaPY',
    'patientPotential',
    'patientUtilisation',
    'percentage',
    'salesUtilisation',
    // 'lastUpdate'
  ];
  public accountLeaderboardDataSource: MatTableDataSource<AccountLeaderboardItem>;

  constructor(
    private _kamReportsService: KamReportsService,
    private _categoriesService: CategoriesService,
    private _pagingService: PagingService,
  ) {}

  get centers(): TCenter[] { return this._kamReportsService.allCenters; }

  ngOnInit(): void {
    this.accountLeaderboardData = this.buildAccountLeaderboardTableData(this.centers);
    const accountLeaderboardAllData = [this.accountLeaderboardData.nationalAverage, ...this.accountLeaderboardData.accounts];
    this.accountLeaderboardDataSource = new MatTableDataSource(accountLeaderboardAllData);
  }

  ngAfterViewInit(): void {
    this.accountLeaderboardDataSource.sort = this.accountLeaderboardSort;
    this.accountLeaderboardDataSource.paginator = this.accountLeaderboardPaginator;
    this.accountLeaderboardDataSource.sortingDataAccessor = (item, property) => {
      const value = item[property];
      // If value is a number, return it; if not, return a value that sorts last
      return typeof value === 'number' ? value : Number.NEGATIVE_INFINITY;
    };
  }

  buildAccountLeaderboardTableData(centers: TCenter[]): AccountLeaderboardTableData {
    // build accounts data
    const accounts: AccountLeaderboardItem[] = [];
    centers.forEach(center => {
      accounts.push(this.buildAccountsLeaderboardItem(center));
    });
    // calculate average
    const noAccounts: number = accounts.length;
    const average: AccountLeaderboardItem = {
      name: 'National average',
      total: 0,
      delta4Month: 0,
      deltaPY: 0,
      patientPotential: 0,
      patientUtilisation: 0,
      percentage: 0,
      salesUtilisation: 0,
      // lastUpdate: new Date('2025-01-01'),
    };

    accounts.forEach(account => {
      average.total = addSmart(average.total, account.total);
      average.delta4Month = addSmart(average.delta4Month, account.delta4Month);
      average.deltaPY = addSmart(average.deltaPY, account.deltaPY);
      average.patientPotential = addSmart(average.patientPotential, account.patientPotential);
      average.patientUtilisation = addSmart(average.patientUtilisation, account.patientUtilisation);
      average.percentage = addSmart(average.percentage, account.percentage);
      average.salesUtilisation = addSmart(average.salesUtilisation, account.salesUtilisation);
    });

    average.total = divideSmart(average.total, noAccounts);
    average.delta4Month = divideSmart(average.delta4Month, noAccounts);
    average.deltaPY = divideSmart(average.deltaPY, noAccounts);
    average.patientPotential = divideSmart(average.patientPotential, noAccounts);
    average.patientUtilisation = divideSmart(average.patientUtilisation, noAccounts);
    average.percentage = divideSmart(average.percentage, noAccounts);
    average.salesUtilisation = divideSmart(average.salesUtilisation, noAccounts);

    const result  = { nationalAverage: average, accounts };
    return result;
  }

  /**
   * Bild data item for Account Leaderboard table
   * @param  {TCenter} center
   * @returns AccountLeaderboardItem
   */
  buildAccountsLeaderboardItem(center: TCenter): AccountLeaderboardItem {
    const centerProjectsData = center.projectsData.filter(pd => pd.projectId === this.project.id)[0];
    const result =  {
      name: center.c_name || center.c_original_name,
      total: +centerProjectsData.sales_latest_period || 'N/A',
      delta4Month: Math.round(+centerProjectsData.sales_momentum_short) || 'N/A',
      deltaPY: Math.round(+centerProjectsData.sales_momentum_long) || 'N/A',
      patientPotential: +centerProjectsData.c_proj_patient_potential1 || 'N/A',
      patientUtilisation: +centerProjectsData.patient_utilisation1 || 'N/A',
      percentage: calculatePercent(+centerProjectsData.patient_utilisation1, +centerProjectsData.c_proj_patient_potential1),
      salesUtilisation: +centerProjectsData.sales_patient_equivalents || 'N/A',
      // lastUpdate: new Date('2025-01-01'),
    };
    return result;
  }

  getHeader(field: string): string {
    return this._categoriesService.getFieldDescription(field, this.project.id);
  }

  getNumber(val: any): number {
    return isNaN(+val) ? 0 : +val;
  }

  // get pagination size options
  getPageSizeOptions(length: number): number[] {
    return this._pagingService.getPageSizeOptions(length);
  }

}
